// Lookup Models
export interface Lookup {
  id: string;
  name: string;
}

// Calendar Frequencies
export interface CalendarFrequency extends Lookup {
  tag: string;
  week_count: number;
}

// Invoice Statuses
export interface InvoiceStatus extends Lookup {
  tag: string;
}

export interface StatementStatus extends Lookup {
  tag: string;
}

// Invoice Status Transitions
export interface InvoiceStatusTransitions extends Lookup {
  to_status: InvoiceStatus;
  from_status: InvoiceStatus;
  to_status_id: string;
  from_status_id: string;
  tag: string;
}

// Client Invoice Statuses
export interface ClientInvoiceStatus extends Lookup {
  tag: string;
  is_active: boolean;
  colour: string;
  sequence: number;
  from_statuses: { to_status_id: string }[];
  to_statuses: { from_status_id: string }[];
}

// Client Invoice Status Transitions
export interface ClientInvoiceStatusTransition {
  id: string;
  to_status: ClientInvoiceStatus;
  from_status: ClientInvoiceStatus;
  to_status_id: string;
  from_status_id: string;
  // When true, a reverse ("unlock") transition — the UI hides these targets
  // from users without `supportathome-claims:Unlock`.
  requires_unlock: boolean;
}

// Sah Budget Types
export interface SahBudgetType extends Lookup {
  sah_reference_id: string;
  tag: string;
}

// Sah Categories
export interface SahCategory extends Lookup {
  id: string;
  sah_reference_id: string;
  sah_id: string;
  max_contrib_proportion?: number;
}

// Sah Claim Statuses
export interface SahClaimStatus extends Lookup {
  tag: string;
  colour: string;
  sequence: number;
}

// Closed set of purchase method values for SaH items (TAV-2344).
export type PurchaseMethodType = 'PURCHASED' | 'LOANED';

// Sah Services
export interface SahService extends Lookup {
  category_id: string;
  sah_id: string;
  start_date: string;
  end_date?: string;
  meta_information?: any;
  first_payment_indicator_required?: boolean;
  prescribed_items_allowed?: boolean;
  // Gate + default for the purchase method selector (TAV-2344). `purchase_method_allowed`
  // controls whether the field is shown; `purchase_method_type` is the service-level
  // default the claim file falls back to when the item leaves it blank.
  purchase_method_allowed?: boolean;
  purchase_method_type?: PurchaseMethodType | null;
}

// Sah Late Submission Reasons (TAV-2592). `sah_id` is Services Australia's own
// `lateSubmissionReasonCode` value (e.g. 'LEAVE') - stored and sent verbatim, never translated.
export interface SahLateSubmissionReason extends Lookup {
  sah_id: string;
  // Services Australia's `longText` - raw vendor HTML. Deliberately not rendered without a
  // sanitiser decision (TAV-2592 follow-up).
  description?: string | null;
  // AC #7's gate: whether a justification is mandatory for this reason. Data-driven - never a
  // hardcoded "Other" string match. Four codes carry `true` (CULTURAL_LEAVE,
  // DECEASED_CARE_RECIPIENT, FIRST_NATIONS_PEOPLE, OTHER), not just "Other".
  free_text_required: boolean;
  // Read but unused here - every reason code currently returns `attachments_required: false`.
  attachments_required: boolean;
}

// Sah Service Wraps / Items
export interface SahServiceWrap {
  id: string;
  service_id: string;
  sah_id: string;
  description: string;
  meta_information?: any;
}
export interface SahServiceItem {
  id: string;
  service_id: string;
  sah_id: string;
  description: string;
  meta_information?: any;
}

export interface SahServiceHpt {
  id: string;
  service_id: string;
  sah_id: string;
  description: string;
  meta_information?: any;
}

// Sah Service Type Associations
// The department's own reference detail for a service, lifted out of the raw vendor row. Shown
// behind the info icon on the associations list - these codes are how Services Australia
// identifies a service and are otherwise invisible in the app.
export interface SahServiceInfo {
  sah_id: string | null;
  group: string | null;
  group_id: string | null;
  service_type: string | null;
  service_type_id: string | null;
  unit_type: string | null;
  // What a supplier invoice or claim item coded against this service will ask for. These are the
  // same flags the entry forms gate their fields on, and two periods of one service can differ.
  item_count: number;
  wrap_count: number;
  hpt_count: number;
  first_payment_indicator_required: boolean;
  prescribed_items_allowed: boolean;
  purchase_method_allowed: boolean;
  purchase_method_type: string | null;
}

export interface SahServiceTypeAssociation extends Lookup {
  category: string;
  category_id?: string;
  service_type_ids: number[];
  service_info?: SahServiceInfo | null;
  // The period this occurrence of the service covers. A service whose contribution category
  // changes on a date exists as two occurrences sharing a name, so the dates are what tells them
  // apart on screen.
  start_date?: string | null;
  end_date?: string | null;
  // Service types that cannot be added to THIS occurrence because they are already associated with
  // a different service whose period overlaps it. A service type associated with a service over a
  // different, non-overlapping period is absent from this list and stays selectable.
  unavailable_service_type_ids?: number[];
}

// Shifts
export interface Shift extends Lookup {
  tag?: string;
  allow_weekdays: number[];
  allow_rostering: boolean;
}

// The full platform shift row as returned by GET /shifts. `Shift` stays the trimmed shape the
// price-plan and budget selectors project; the band times and precedence are only needed where a
// company's own times are merged over the platform defaults.
export interface ShiftWithTimes extends Shift {
  tag: string;
  start_time: string;
  end_time: string;
  priority: number;
}

export interface CompanyShift {
  id: string;
  company_id: number;
  shift_id: string;
  start_time: string;
  end_time: string;
  // Carried by the payload but never customer-set - the API writes the platform band's precedence.
  priority: number;
}

// Funding Level Amounts
export interface FundingLevelAmount {
  id: string;
  sah_level: number;
  daily_amount: number;
}

export interface UserLanguage {
  clientFormOptionName: string;
}

// Supplements

export interface SupplementRate {
  id: string;
  label: string;
  type: 'budget' | 'casemanagement';
  start_date: string;
  end_date?: string;
  rate: number;
  rate_type: 'base' | 'per-day';
}

export interface Supplement {
  id?: string;
  supplement_id: string;
  budget_id?: string;
  amount: number;
  supplement?: SupplementRate;
}

/**
 * A supplement rate is stale when it carries an `end_date` — the lookup query only returns active
 * (`end_date IS NULL`) rows, so any rate with an end_date came from a budget's own selected-but-expired
 * snapshot. Single source of truth for the staleness rule across the budget supplement components.
 */
export const isSupplementRateStale = (rate?: SupplementRate | null): boolean => rate?.end_date != null;
